import {
    Button, Dialog, DialogActions, DialogContent
} from '@mui/material';
import {
    ReactNode, createContext, useContext, useMemo, useRef, useState
} from 'react';


interface ConfirmDialogContextValue {
    open: boolean;
    dialogContent: ReactNode;
    showDialog: (content: ReactNode) => Promise<boolean>;
    closeDialog: (confirmed: boolean) => void;
}

export const ConfirmDialogContext = createContext<ConfirmDialogContextValue>({
    open: false,
    dialogContent: null,
    showDialog: () => Promise.resolve(true),
    closeDialog: () => {}
});

/**
 * Custom hook to manage state for the confirm dialog. The return value of this hook
 * should be passed as the value prop to ConfirmDialogContext.Provider at the root
 * of the app.
 *
 * @example
 * ```ts
 * const confirmDialogValue = useConfirmDialogValue();
 *
 * return (
 *     <ConfirmDialogContext.Provider value={confirmDialogValue}>
 *         // Render the rest of the app here
 *     </ConfirmDialogContext.Provider>
 * );
 * ```
 */
export function useConfirmDialogValue(): ConfirmDialogContextValue {
    const [ open, setOpen ] = useState(false);
    const [ dialogContent, setDialogContent ] = useState<ReactNode>();
    const resolveOpenPromiseRef = useRef<(value: boolean) => void>();

    const showDialog = useMemo(() => (content: ReactNode) => {
        setDialogContent(content);
        setOpen(true);

        return new Promise<boolean>((resolve) => {
            resolveOpenPromiseRef.current = resolve;
        });
    }, []);

    return useMemo(() => ({
        open,
        dialogContent,
        showDialog,
        closeDialog: (confirmed) => {
            resolveOpenPromiseRef.current?.(confirmed);
            setOpen(false);
        }
    }), [
        open, dialogContent, showDialog
    ]);
}

/**
 * Renders the confirmation dialog
 * @constructor
 */
export function ConfirmDialog() {
    const { closeDialog, dialogContent, open } = useContext(ConfirmDialogContext);

    return (
        <Dialog
            open={open}
            maxWidth="xs"
        >
            <DialogContent>
                {dialogContent}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => closeDialog(false)}>
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => closeDialog(true)}
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Custom hook that returns a confirm() function to display the dialog anywhere
 * in the app. Any ReactNode can be passed to the returned function
 *
 * @example
 * ```ts
 * const confirm = useConfirm();
 *
 * async function someAsyncFunction() {
 *     if(await confirm('Are you sure?')) {
 *         // User confirmed, do stuff
 *     }
 * }
 *
 * // Multiline example
 * async function multiLineConfirm() {
 *     if(await confirm(
 *         <Typography>
 *             This action has consequences!
 *             <br /><br />
 *             Are you sure you want to do it?
 *         </Typography>
 *     )) {
 *         // User confirmed, do stuff
 *     }
 * }
 * ```
 */
export function useConfirm() {
    const { showDialog } = useContext(ConfirmDialogContext);

    return showDialog;
}
